import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdDragIndicator } from 'react-icons/md';
import {
  updateItem,
  deleteItem,
  createSibling,
  createChild,
  dragAndDrop,
} from '../../../actions/programModules';
import TreeItemForm from './TreeItemForm';
import ConfirmModal from './ConfirmModal';
import styles from './styles.module.scss';

// Иконки
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Глобальное состояние для drag
let currentDragData = null;

// Линии дерева
const TreeLines = ({ level, connectorLevels, isLastChild, indentSize }) => {
  if (level === 0) return null;

  const lines = [];

  connectorLevels.forEach(connectorLevel => {
    lines.push(
      <span
        key={`v-${connectorLevel}`}
        className={styles.treeLine}
        style={{ left: `${connectorLevel * indentSize + 14}px` }}
      />
    );
  });

  const currentLeft = (level - 1) * indentSize + 14;
  lines.push(
    <span
      key="current"
      className={`${styles.treeLine} ${isLastChild ? styles.lastChild : ''}`}
      style={{ left: `${currentLeft}px` }}
    />,
    <span
      key="horizontal"
      className={styles.treeLineHorizontal}
      style={{ left: `${currentLeft}px` }}
    />
  );

  return <div className={styles.treeLines}>{lines}</div>;
};

const TreeRow = ({ node, index, indentSize, onToggleExpand }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.programModules.loading);
  const [formModal, setFormModal] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropPosition, setDropPosition] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Глобальный слушатель для сброса dropPosition при завершении любого drag
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      setDropPosition(null);
      setIsDragging(false);
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);

  const handleEdit = () => setFormModal('edit');
  const handleAddSibling = () => setFormModal('addSibling');
  const handleAddChild = () => setFormModal('addChild');

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteItem(node.oid));
    setShowDeleteConfirm(false);
  };

  // Подсчёт вложенных элементов
  const countChildren = (children) => {
    if (!children || children.length === 0) return 0;
    return children.reduce((acc, child) => {
      return acc + 1 + countChildren(child.children);
    }, 0);
  };

  const nestedCount = node.children ? countChildren(node.children) : 0;

  const handleFormSubmit = (formData) => {
    switch (formModal) {
      case 'edit':
        dispatch(updateItem(node.oid, formData));
        break;
      case 'addSibling':
        dispatch(createSibling(node.oid, formData));
        break;
      case 'addChild':
        dispatch(createChild(node.oid, formData));
        break;
      default:
        break;
    }
    setFormModal(null);
  };

  const getFormTitle = () => {
    switch (formModal) {
      case 'edit':
        return 'Редактирование';
      case 'addSibling':
        return 'Добавить на том же уровне';
      case 'addChild':
        return 'Добавить дочерний элемент';
      default:
        return '';
    }
  };

  // Drag на всю строку
  const handleDragStart = (e) => {
    currentDragData = { oid: node.oid, level: node.level };
    e.dataTransfer.setData('text/plain', JSON.stringify(currentDragData));
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDropPosition(null);
    currentDragData = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();

    if (!currentDragData) return;
    if (currentDragData.oid === node.oid) {
      setDropPosition(null);
      return;
    }
    if (currentDragData.level !== node.level) {
      e.dataTransfer.dropEffect = 'none';
      setDropPosition(null);
      return;
    }

    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const threshold = rect.height / 2;

    setDropPosition(y < threshold ? 'before' : 'after');
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropPosition(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();

    if (!currentDragData) {
      setDropPosition(null);
      return;
    }

    const { oid: dragOid, level: dragLevel } = currentDragData;

    if (dragLevel !== node.level || dragOid === node.oid) {
      setDropPosition(null);
      return;
    }

    if (dropPosition === 'before' && node.isFirst) {
      dispatch(dragAndDrop(dragOid, node.parentOid || 0, true));
    } else if (dropPosition === 'before') {
      const siblings = node.siblings;
      const currentIndex = siblings.findIndex(s => s.oid === node.oid);
      if (currentIndex > 0) {
        dispatch(dragAndDrop(dragOid, siblings[currentIndex - 1].oid, false));
      }
    } else if (dropPosition === 'after') {
      dispatch(dragAndDrop(dragOid, node.oid, false));
    }

    setDropPosition(null);
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    if (node.hasChildren) {
      onToggleExpand(node.oid);
    }
  };

  const rowClasses = [
    styles.gridRow,
    isDragging ? styles.dragging : '',
    dropPosition ? styles.dropTarget : '',
    index % 2 === 1 ? styles.even : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        className={rowClasses}
        style={{ animationDelay: `${Math.min(index * 0.02, 0.3)}s` }}
        draggable={!loading}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-drop-position={dropPosition}
      >
        {/* Первая ячейка - drag handle + номер */}
        <div className={styles.indexCell}>
          <span className={styles.dragHandle}>
            <MdDragIndicator />
          </span>
          {node.title_index ? (
            <span className={styles.numberBadge} title={node.title_index}>{node.title_index}</span>
          ) : null}
        </div>

        {/* Вторая ячейка - наименование */}
        <div className={styles.gridCell}>
          <div
            className={styles.nameCell}
            style={{ paddingLeft: `${node.level * indentSize}px` }}
          >
            <TreeLines
              level={node.level}
              connectorLevels={node.connectorLevels}
              isLastChild={node.isLastChild}
              indentSize={indentSize}
            />

            {node.hasChildren ? (
              <button
                className={`${styles.expandBtn} ${node.isExpanded ? styles.expanded : ''}`}
                onClick={handleExpandClick}
                type="button"
              >
                <ChevronIcon />
              </button>
            ) : (
              <span className={styles.leafMarker} />
            )}

            <span className={`${styles.nodeName} ${node.hasChildren ? styles.parent : styles.leaf}`}>
              {node.title}
            </span>
          </div>
        </div>

        {/* Третья ячейка - описание */}
        <div className={styles.gridCell}>
          {node.description && (
            <span className={styles.itemDescription} title={node.description}>
              {node.description}
            </span>
          )}
        </div>

        {/* Четвёртая ячейка - действия */}
        <div className={styles.gridCell}>
          <div className={styles.actions}>
            <button
              className={styles.actionButton}
              onClick={handleEdit}
              disabled={loading}
              title="Редактировать"
              type="button"
            >
              ✎
            </button>
            <button
              className={styles.actionButton}
              onClick={handleAddSibling}
              disabled={loading}
              title="Добавить на том же уровне"
              type="button"
            >
              +
            </button>
            <button
              className={styles.actionButton}
              onClick={handleAddChild}
              disabled={loading}
              title="Добавить дочерний"
              type="button"
            >
              ↳
            </button>
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={handleDelete}
              disabled={loading}
              title="Удалить"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {formModal && (
        <TreeItemForm
          item={formModal === 'edit' ? node : null}
          title={getFormTitle()}
          onSubmit={handleFormSubmit}
          onClose={() => setFormModal(null)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Удаление элемента"
          itemTitle={node.title}
          nestedCount={nestedCount}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </>
  );
};

export default TreeRow;
